




































































import { computed, defineComponent, ref } from "@vue/composition-api";
import { PaymentPackage } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { SubscriptionLevel } from "@scrile/api-provider/dist/api/SubscriptionLevelProvider";
import { ContentFeedSearchResult } from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import { checkRole } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAuthUser from "@/hooks/useAuthUser";
import useSystemSettings from "@/hooks/useSystemSettings";
import historyManager from "@/lib/historyManager";
import { $t } from "@/lib/i18nHelpers";
import { getAbsUrl, getProfileUrl, shareUrl } from "@/lib/urlHelpers";
import config from "@/config";
import { BlockingStatus } from "@/types";
import BaseButton from "@/components/BaseButton";
import BaseIcon from "@/components/BaseIcon";
import BaseMenu from "@/components/BaseMenu";
import BasePage from "@/components/BasePage";
import BlockUserController from "@/components/BlockUserController";
import ConfirmSensitiveContent from "@/components/ConfirmSensitiveContent";
import ContentFeedView from "@/components/ContentFeedView";
import CreatePostButtonController from "@/components/CreatePostButton";
import EmptyContent from "@/components/EmptyContent";
import PageTitle from "@/components/PageTitle";
import SuggestedCreators from "@/components/SuggestedCreators";
import ProfileInfoView from "../ProfileInfoView";
import VerificationNotification from "../VerificationNotification";

export default defineComponent({
  name: "PageProfileView",
  components: {
    CreatePostButtonController,
    ConfirmSensitiveContent,
    ProfileInfoView,
    EmptyContent,
    BasePage,
    PageTitle,
    BaseIcon,
    BaseButton,
    BaseMenu,
    ContentFeedView,
    VerificationNotification,
    BlockUserController,
    SuggestedCreators,
  },
  props: {
    canUpdateContentSet: { type: Boolean, required: true },
    feedId: { type: String },
    loading: { type: Boolean, required: true },
    scroll: { type: Boolean, required: true },
    isAuthor: { type: Boolean, required: true },
    isFollowing: { type: Number as () => 0 | 1 | -1, required: true },
    isSubscribed: { type: Boolean, required: true },
    user: { type: Object as () => UserPublicData },
    questionaryInfo: { type: Object as () => Record<string, any>, required: true },
    contentFeedSearchResult: { type: Object as () => ContentFeedSearchResult },
    subscriptionLevels: { type: Array as () => SubscriptionLevel[], required: true },
    paymentPackages: { type: Array as () => PaymentPackage[], required: true },
  },
  setup(props, { root }) {
    const { userVerificationStatus: authUserVerificationStatus, authUser } = useAuthUser();
    const { systemSettings } = useSystemSettings();

    const blockingStatus = ref<BlockingStatus>();
    const userId = computed(() => props.user?.id ?? "");
    const profileUrl = computed(() => (props.user && getProfileUrl(props.user)) || undefined);
    const isEmptyProfile = computed(() => !props.loading && props.contentFeedSearchResult?.result.length === 0);
    const isDisabled = computed(() => props.user?.disabled ?? true);
    const isBlocked = computed(() => blockingStatus.value?.authUser);
    const isSensitiveContent = computed(() => props.questionaryInfo.sensitiveContent);
    const hasContentCount = computed(() =>
      props.subscriptionLevels.some(level => level.contentCount && level.contentCount > 0)
    );
    const isMember = computed(() => props.user?.role === config.userRegistrationRole);
    const showSuggestedUsers = computed(
      () => checkRole(authUser.value?.role ?? "", "member") && checkRole(props.user?.role ?? "", "creator")
    );
    const allowCreatePost = computed(() => {
      if (!props.user) {
        return false;
      } else if (systemSettings.value?.content?.everyoneCanPost) {
        return true;
      }
      return props.canUpdateContentSet;
    });
    const menuItems = computed(() => {
      const menuItems = [];
      menuItems.push({
        name: "copy-link",
        text: $t("Copy link"),
      });
      return menuItems;
    });
    const hasHistory = computed(() => historyManager.hasHistory());

    const onFeedOnScreen = (feedId: string | null) => {
      if (feedId === props.feedId || !props.user) return;
      root.$router.replace(getProfileUrl(props.user, (feedId && { feedId }) || undefined));
    };

    const onConfirmSensitive = (sensitiveContent: boolean) => {
      if (!sensitiveContent) {
        root.$router.replace({ name: "home" });
      }
    };

    const copyLink = () => shareUrl(getAbsUrl(`${root.$route.path}`), $t("Link copied"));

    return {
      authUserVerificationStatus,
      userId,
      profileUrl,
      isEmptyProfile,
      allowCreatePost,
      blockingStatus,
      isBlocked,
      isDisabled,
      isSensitiveContent,
      isMember,
      onFeedOnScreen,
      onConfirmSensitive,
      hasContentCount,
      menuItems,
      copyLink,
      hasHistory,
      showSuggestedUsers,
    };
  },
});
